import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check, HelpCircle } from 'lucide-react';
import PublicPageShell from '../components/PublicPageShell';
import NotFound from './NotFound';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getRelatedResources,
  getRelatedServices,
  getLocalizedServiceBySlug,
  resourcePath,
  servicePath,
} from '../data/contentLibrary';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../utils/analytics';

const BulletList = ({ items }) => (
  <ul className="mt-5 space-y-3">
    {items.map((item) => (
      <li key={item} className="flex gap-3 text-sm leading-6 text-white/62">
        <Check className="mt-0.5 h-5 w-5 flex-none text-[#00FFD1]" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const ContentBlock = ({ title, children }) => (
  <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6">
    <h2 className="text-2xl font-semibold text-white">{title}</h2>
    {children}
  </section>
);

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const { currentLang, t } = useLanguage();
  const service = getLocalizedServiceBySlug(slug, currentLang);

  useEffect(() => {
    if (service) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.SERVICE_VIEWED, {
        serviceSlug: service.slug,
      });
    }
  }, [service]);

  if (!service) return <NotFound />;

  const relatedServices = getRelatedServices(service.relatedServices, currentLang);
  const relatedResources = getRelatedResources(service.relatedResources, currentLang);

  const trackCta = (ctaLocation) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.SERVICE_CTA_CLICKED, {
      serviceSlug: service.slug,
      ctaLocation,
    });
  };

  return (
    <PublicPageShell mainClassName="pt-20">
      <article className="relative overflow-hidden bg-black">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-[1180px] px-5 py-16 md:px-[7.6923%] md:py-24">
          <nav className="mb-8 flex flex-wrap gap-2 text-sm text-white/44" aria-label={t('common.breadcrumb')}>
            <Link to="/" className="hover:text-[#00FFD1]">{t('nav.home')}</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-[#00FFD1]">{t('nav.services')}</Link>
            <span>/</span>
            <span className="text-white/70">{service.title}</span>
          </nav>

          <header className="max-w-4xl">
            <p className="mb-4 text-sm font-semibold uppercase text-[#00FFD1]">
              {t('common.serviceEyebrow')}
            </p>
            <h1 className="text-4xl font-semibold leading-tight text-white md:text-6xl">
              {service.h1}
            </h1>
            <p className="mt-6 text-base leading-8 text-white/64 md:text-lg">
              {service.intro}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/book-call"
                onClick={() => trackCta('hero_book_call')}
                className="btn-glass-primary"
              >
                {t('nav.bookConsultation')}
              </Link>
              <Link
                to="/contact"
                onClick={() => trackCta('hero_contact')}
                className="btn-glass-secondary"
              >
                {t('nav.contactWebouse')}
              </Link>
            </div>
          </header>

          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            <ContentBlock title={service.sectionLabels?.fit || 'Who This Service Fits'}>
              <BulletList items={service.fit} />
            </ContentBlock>
            <ContentBlock title={service.sectionLabels?.problems || 'Business Problems It Solves'}>
              <BulletList items={service.problems} />
            </ContentBlock>
            <ContentBlock title={service.sectionLabels?.deliverables || 'Included Deliverables'}>
              <BulletList items={service.deliverables} />
            </ContentBlock>
            <ContentBlock title={service.sectionLabels?.exclusions || 'Not Included Unless Scoped'}>
              <BulletList items={service.exclusions} />
            </ContentBlock>
          </div>

          <div className="mt-5 space-y-5">
            <ContentBlock title={service.sectionLabels?.approach || 'Webouse Approach'}>
              <div className="mt-5 space-y-4 text-sm leading-7 text-white/62">
                {service.approach.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </ContentBlock>

            <ContentBlock title={service.sectionLabels?.stages || 'Typical Project Stages'}>
              <ol className="mt-5 grid gap-3 md:grid-cols-2">
                {service.stages.map((stage, index) => (
                  <li key={stage} className="rounded-xl border border-white/[0.07] bg-black/40 p-4 text-sm leading-6 text-white/62">
                    <span className="mb-2 block text-xs font-semibold uppercase text-[#00FFD1]">
                      {(service.sectionLabels?.stage || 'Stage')} {index + 1}
                    </span>
                    {stage}
                  </li>
                ))}
              </ol>
            </ContentBlock>

            <div className="grid gap-5 lg:grid-cols-3">
              <ContentBlock title={service.sectionLabels?.timeline || 'Timeline Approach'}>
                <p className="mt-5 text-sm leading-7 text-white/62">{service.timeline}</p>
              </ContentBlock>
              <ContentBlock title={service.sectionLabels?.pricing || 'Pricing Approach'}>
                <p className="mt-5 text-sm leading-7 text-white/62">{service.pricing}</p>
              </ContentBlock>
              <ContentBlock title={service.sectionLabels?.technology || 'Technology Approach'}>
                <p className="mt-5 text-sm leading-7 text-white/62">{service.technology}</p>
              </ContentBlock>
            </div>

            <ContentBlock title={service.sectionLabels?.faq || 'Frequently Asked Questions'}>
              <div className="mt-5 grid gap-4">
                {service.faq.map((item) => (
                  <div key={item.question} className="rounded-xl border border-white/[0.07] bg-black/40 p-5">
                    <h3 className="flex items-start gap-3 text-lg font-semibold text-white">
                      <HelpCircle className="mt-0.5 h-5 w-5 flex-none text-[#00FFD1]" />
                      {item.question}
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-white/62">{item.answer}</p>
                  </div>
                ))}
              </div>
            </ContentBlock>
          </div>

          <aside className="mt-12 grid gap-5 lg:grid-cols-2">
            <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6">
              <h2 className="text-2xl font-semibold text-white">{t('common.relatedServices')}</h2>
              <div className="mt-5 grid gap-3">
                {relatedServices.map((item) => (
                  <Link
                    key={item.slug}
                    to={servicePath(item.slug)}
                    className="group flex items-center justify-between rounded-xl border border-white/[0.07] bg-black/40 px-4 py-3 text-white/68 hover:border-[#00FFD1]/35 hover:text-white"
                  >
                    <span>{item.title}</span>
                    <ArrowRight className="h-4 w-4 text-[#00FFD1] transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6">
              <h2 className="text-2xl font-semibold text-white">{t('common.relatedResources')}</h2>
              <div className="mt-5 grid gap-3">
                {relatedResources.map((item) => (
                  <Link
                    key={item.slug}
                    to={resourcePath(item.slug)}
                    className="group flex items-center justify-between rounded-xl border border-white/[0.07] bg-black/40 px-4 py-3 text-white/68 hover:border-[#00FFD1]/35 hover:text-white"
                  >
                    <span>{item.title}</span>
                    <ArrowRight className="h-4 w-4 text-[#00FFD1] transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </section>
          </aside>

          <footer className="mt-12 rounded-2xl border border-[#00FFD1]/20 bg-[#00FFD1]/[0.04] p-6">
            <p className="text-sm leading-6 text-white/62">
              {t('common.lastUpdated')}: {service.lastUpdated}
            </p>
            <p className="mt-1 text-sm leading-6 text-white/62">
              {t('common.contentOwner')}: {service.owner}
            </p>
            <Link
              to="/book-call"
              onClick={() => trackCta('footer_book_call')}
              className="mt-6 inline-flex btn-glass-primary"
            >
              {t('nav.bookConsultation')}
            </Link>
          </footer>
        </div>
      </article>
    </PublicPageShell>
  );
};

export default ServiceDetailPage;
